import { useCallback, useState } from 'react'

import { ITagTextBackgroundSliceData } from 'components/MCTextEditor/formatting/interfaces/tagTextBackgroundSliceData'
import { SliceType } from 'components/MCTextEditor/slice/types'
import { IProcessorState, ISlice } from 'components/RichTextEditor/processor/constants'

export interface HighlightDefinition {
  id: string
  text: string
}

export const applyInitialHighlights = (
  text: string,
  highlights: readonly HighlightDefinition[],
): string => {
  let result = text

  for (const highlight of highlights) {
    result = result.replace(
      highlight.text,
      `<mc-background color="#e6e1ff" anchor="${highlight.id}" highlightId="${highlight.id}">${highlight.text}</mc-background>`,
    )
  }

  return result
}

export const useHighlight = ({
  value,
  highlightedChunks,
}: {
  value: string
  highlightedChunks: HighlightDefinition[]
}) => {
  const [editorValue] = useState(applyInitialHighlights(value, highlightedChunks))

  const customProcessor = useCallback(
    (processorState: IProcessorState) => {
      const mappedSlices = processorState.slices.map((slice) => {
        if (slice.type !== SliceType.TEXT_BACKGROUND) {
          return slice
        }

        const sliceData = slice.data as ITagTextBackgroundSliceData

        const initialHighlight = highlightedChunks.find(
          (highlight) => highlight.id === sliceData.highlightId,
        )

        if (!initialHighlight) {
          return slice
        }

        const currentSliceValue = processorState.text.slice(slice.range.start, slice.range.end)

        return initialHighlight.text === currentSliceValue
          ? { ...slice, data: { ...sliceData, color: '#e6e1ff' } }
          : { ...slice, data: { ...sliceData, color: 'var(--color-neutral-50)' } }
      })

      return {
        ...processorState,
        slices: mappedSlices,
      }
    },
    [highlightedChunks],
  )

  const filterPasteSlices = useCallback(
    (pasteSlices: ISlice[], existingSlices: readonly ISlice[]) => {
      const existingHighlightIds = new Set(
        existingSlices
          .filter((s) => s.type === SliceType.TEXT_BACKGROUND)
          .map((s) => (s.data as ITagTextBackgroundSliceData).highlightId),
      )

      return pasteSlices.filter((slice) => {
        if (slice.type !== SliceType.TEXT_BACKGROUND) {
          return false
        }

        const data = slice.data as ITagTextBackgroundSliceData
        return !existingHighlightIds.has(data.highlightId)
      })
    },
    [],
  )

  return {
    editorValue,
    filterPasteSlices,
    customProcessor,
  }
}
